import { useState, useCallback } from "react";
import { addToast } from "@heroui/react";
import { useBoundStore } from "@/app/store";
import { useShallow } from "zustand/shallow";
import { hooks } from "@/services/api/hooks";
import { createVault } from "../utils/vault";
import { delay } from "@/shared/utils/delay";

const STEP_DELAY_MS = 500;
const ENTRY_UPDATE_DELAY_MS = 100;

export function useSetupEncryption() {
  const [isSetup, setIsSetup] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");

  const { deletedNotes, notes, updateNote, todos, updateTodo, tags, updateTag, folders, updateFolder } = useBoundStore(
    useShallow((state) => ({
      deletedNotes: state.notes.deleted,
      notes: state.notes.main,
      updateNote: state.notes.updateNote,
      todos: state.todos.items,
      updateTodo: state.todos.updateTodo,
      tags: state.tags.list,
      updateTag: state.tags.updateTag,
      folders: state.folders.main,
      updateFolder: state.folders.updateFolder,
    }))
  );

  const { appSettings, updateSettings } = useBoundStore(
    useShallow((state) => ({ appSettings: state.settings.app, updateSettings: state.settings.updateSettings }))
  );

  const { userId } = useBoundStore(useShallow((state) => ({ userId: state.user.id })));
  const { mutateAsync: addVaultKey } = hooks.vaultKeys.useAddVaultKey();

  const resetProgress = useCallback(() => {
    setIsSetup(false);
    setProgress(0);
    setProgressMessage("");
  }, []);

  const handleSetupEncryption = useCallback(
    async (password: string) => {
      setIsSetup(true);
      setProgress(0);
      setProgressMessage("");

      try {
        if (!appSettings || !userId) {
          return;
        }

        // Create the vault with the master password
        setProgressMessage("Creating encryption vault...");
        setProgress(5);
        const { wrappedState } = await createVault(password);
        await delay(STEP_DELAY_MS);

        setProgressMessage("Saving vault key...");
        setProgress(10);
        await addVaultKey({ ...wrappedState, user_id: userId });
        await delay(STEP_DELAY_MS);

        // Re-encrypt all existing data
        const totalItems = deletedNotes.length + notes.length + todos.length + tags.length + folders.length;
        let processedItems = 0;

        const updateProgress = async () => {
          processedItems += 1;
          const newProgress = 15 + Math.floor((processedItems / totalItems) * 70);
          await delay(ENTRY_UPDATE_DELAY_MS);
          setProgress(newProgress);
        };

        setProgressMessage("Encrypting notes...");
        for (const note of notes) {
          await updateNote(
            note.id,
            { text_content: note.text_content, html_content: note.html_content },
            Boolean(note.public_uuid)
          );
          await updateProgress();
        }
        await delay(STEP_DELAY_MS);

        setProgressMessage("Encrypting todos...");
        for (const todo of todos) {
          await updateTodo(todo.id, { text: todo.text });
          await updateProgress();
        }
        await delay(STEP_DELAY_MS);

        setProgressMessage("Encrypting tags...");
        for (const tag of tags) {
          await updateTag(tag.id, { name: tag.name });
          await updateProgress();
        }
        await delay(STEP_DELAY_MS);

        setProgressMessage("Encrypting folders...");
        for (const folder of folders) {
          await updateFolder(folder.id, { name: folder.name });
          await updateProgress();
        }
        await delay(STEP_DELAY_MS);

        setProgressMessage("Encrypting deleted notes...");
        for (const deletedNote of deletedNotes) {
          await updateNote(
            deletedNote.id,
            { text_content: deletedNote.text_content, html_content: deletedNote.html_content },
            Boolean(deletedNote.public_uuid)
          );
          await updateProgress();
        }
        await delay(STEP_DELAY_MS);

        setProgressMessage("Updating settings...");
        setProgress(90);
        await updateSettings(userId, { app: { ...appSettings, encryption: true } });
        await delay(STEP_DELAY_MS);

        setProgressMessage("Finalizing...");
        setProgress(95);
        await delay(STEP_DELAY_MS);

        setProgress(100);
        setProgressMessage("Complete!");
        await delay(STEP_DELAY_MS);

        resetProgress();
      } catch (error) {
        console.error("Error setting up encryption:", error);
        resetProgress();
        addToast({
          title: "Error setting up encryption",
          description: "There was an error setting up your encryption. Please try again.",
          color: "danger",
        });
      }
    },
    [
      appSettings,
      userId,
      deletedNotes,
      notes,
      todos,
      tags,
      folders,
      addVaultKey,
      updateNote,
      updateTodo,
      updateTag,
      updateFolder,
      updateSettings,
      resetProgress,
    ]
  );

  return {
    isSetup,
    progress,
    progressMessage,
    handleSetupEncryption,
    resetProgress,
  };
}
